import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { publicRequest, privateRequest } from "../utils/api";

export interface AnnouncementTranslation {
  id?: number;
  announcement_id?: number;
  locale: string;
  title: string;
  body: string;
}

export interface Announcement {
  id: string;
  title: string;
  body: string;
  is_pinned?: boolean;
  visible_from?: string;
  visible_to?: string;
  created_at?: string;
  updated_at?: string;
  translations?: AnnouncementTranslation[];
  created_by?: {
    id: number;
    name: string;
  };
  updated_by?: {
    id: number;
    name: string;
  };
}

export interface AnnouncementsResponse {
  announcements: Announcement[];
  pagination?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  message?: string;
  status?: string;
}

export interface AnnouncementResponse {
  announcement: Announcement;
  message?: string;
  status?: string;
}

export interface AnnouncementsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  is_pinned?: boolean;
  latest?: boolean;
  admin?: string;
}

// Query keys
export const ANNOUNCEMENTS_QUERY_KEY = ["announcements"];
export const ANNOUNCEMENT_QUERY_KEY = (id: number) => ["announcements", id];

// Fetch announcements with pagination
const fetchAnnouncements = async (
  params: AnnouncementsQueryParams = {}
): Promise<AnnouncementsResponse> => {
  try {
    const response = await publicRequest.get("/announcements", { params });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching announcements:", error);
    throw error;
  }
};

// Fetch single announcement
const fetchAnnouncement = async (id: number): Promise<Announcement> => {
  try {
    const response = await publicRequest.get(`/announcements/${id}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching announcement:", error);
    throw error;
  }
};

// Fetch announcement for admin (with translations)
const fetchAnnouncementAdmin = async (id: number): Promise<Announcement> => {
  try {
    const response = await privateRequest.get(`/announcements/${id}?admin=true`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching announcement for admin:", error);
    throw error;
  }
};

// Create announcement (admin only)
const createAnnouncement = async (announcementData: {
  is_pinned?: boolean;
  visible_from?: string;
  visible_to?: string;
  translations: AnnouncementTranslation[];
}): Promise<AnnouncementResponse> => {
  try {
    const response = await privateRequest.post("/announcements", announcementData);
    return response.data.data;
  } catch (error) {
    console.error("Error creating announcement:", error);
    throw error;
  }
};

// Update announcement (admin only)
const updateAnnouncement = async ({
  id,
  announcementData,
}: {
  id: number;
  announcementData: {
    is_pinned?: boolean;
    visible_from?: string;
    visible_to?: string;
    translations?: AnnouncementTranslation[];
  };
}): Promise<AnnouncementResponse> => {
  try {
    const response = await privateRequest.put(
      `/announcements/${id}`,
      announcementData
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating announcement:", error);
    throw error;
  }
};

// Delete announcement (admin only)
const deleteAnnouncement = async (id: number): Promise<void> => {
  try {
    await privateRequest.delete(`/announcements/${id}`);
  } catch (error) {
    console.error("Error deleting announcement:", error);
    throw error;
  }
};

// Custom hooks
export const useAnnouncements = (params: AnnouncementsQueryParams = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  
  const result = useQuery({
    queryKey: [...ANNOUNCEMENTS_QUERY_KEY, { ...params, locale: currentLanguage }],
    queryFn: () => fetchAnnouncements({ ...params, locale: currentLanguage }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return result;
};

// Admin hook that includes translations
export const useAnnouncementsAdmin = (params: AnnouncementsQueryParams = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  
  const result = useQuery({
    queryKey: [...ANNOUNCEMENTS_QUERY_KEY, "admin", { ...params, locale: currentLanguage }],
    queryFn: () => fetchAnnouncements({ ...params, locale: currentLanguage, admin: "true" }),
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });

  return result;
};

export const useAnnouncement = (id: number, enabled: boolean = true) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  
  return useQuery({
    queryKey: [...ANNOUNCEMENT_QUERY_KEY(id), currentLanguage],
    queryFn: () => fetchAnnouncement(id),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useAnnouncementAdmin = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...ANNOUNCEMENT_QUERY_KEY(id), "admin"],
    queryFn: () => fetchAnnouncementAdmin(id),
    enabled,
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
    },
  });
};

export const useUpdateAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
    },
  });
};

export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
    },
  });
};

